import { API, Auth } from 'aws-amplify';
import { path } from 'd3-path';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import awsConfig from '@config/aws';
import * as constants from '@config/constants';

API.configure(awsConfig);

const constructPath = (path, useV2) => {
  if (!useV2) {
    return path;
  }

  const api_version = process.env.REACT_APP_API_VERSION;
  switch (api_version) {
    case 'v2':
      return `${path}/${process.env.REACT_APP_CUSTOMER_ID}`;
    default:
      return path;
  }
};

const addParams = (path, params) => {
  if (params) {
    return `${path}?${params}`;
  }
  return path;
};

const useAPI = (
  apiMethod,
  apiName,
  path,
  initialTimestamp = '',
  useBearer = false,
  params = '',
  useV2 = true,
  body = {}
) => {
  const dispatch = useDispatch();
  const apiKey = useSelector(state => state.keys.key);

  const screenPath = constructPath(path, useV2);
  const finalPath = addParams(screenPath, params);

  const url = `${apiName}-${finalPath}`;
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timestamp, setTimestamp] = useState(initialTimestamp);

  // Sync the internal timestamp when the caller supplies a new
  // initialTimestamp (e.g. Date.now() to force a re-fetch).
  // Existing callers pass '' which is falsy, so this is a no-op for them.
  useEffect(() => {
    if (initialTimestamp) {
      setTimestamp(initialTimestamp);
    }
  }, [initialTimestamp]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      setResponse(null);
      useDispatch && dispatch({ type: constants.XHR_IN_PROGRESS, key: url });

      const requestConfig = {
        headers: {
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          'x-api-key': apiKey,
        },
      };
      API.get(apiName, finalPath, requestConfig)
        .then(response => {
          setResponse(response);
          setIsLoading(false);
          useDispatch &&
            dispatch({
              type: constants.XHR_COMPLETE,
              key: url,
              payload: response,
            });
        })
        .catch(error => {
          console.error(
            'Error with API:',
            error.response?.data?.error || error
          );
          setIsLoading(false);

          setError((error.response?.data?.error || error).toString());
          dispatch({
            type: constants.XHR_COMPLETE,
            key: url,
            payload: { error },
          });
        });
    };
    const fetchData = async (apiFn) => {
      setIsLoading(true);
      setResponse(null);
      useDispatch && dispatch({ type: constants.XHR_IN_PROGRESS, key: url });

      const requestConfig = {
        headers: {
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          'x-api-key': apiKey,
          'Content-Type': body?.file
            ? 'multipart/form-data'
            : 'application/json',
        },
        body,
      };
      apiFn(apiName, finalPath, requestConfig)
        .then(response => {
          setResponse(response);
          setIsLoading(false);
          useDispatch &&
            dispatch({
              type: constants.XHR_COMPLETE,
              key: url,
              payload: response,
            });
        })
        .catch(error => {
          console.error(
            'Error with API:',
            error.response?.data?.error || error
          );
          setIsLoading(false);

          setError((error.response?.data?.error || error).toString());
          dispatch({
            type: constants.XHR_COMPLETE,
            key: url,
            payload: { error },
          });
        });
    };
    if (path) {
      if (apiMethod === 'get') {
        getData();
      } else if (apiMethod === 'post') {
        fetchData(API.post.bind(API));
      } else if (apiMethod === 'put') {
        fetchData(API.put.bind(API));
      }
    }
  }, [apiMethod, apiName, path, timestamp, apiKey, params]);
  return {
    response,
    invalidateCache: () => setTimestamp(Date.now()),
    error,
    isLoading,
  };
};

export default useAPI;
